import React, { useState } from 'react'
import { PanelRightClose, PanelRightOpen } from 'lucide-react'
import { cn } from 'lib/utils'
import { Sheet } from 'components/ui/sheet'
import useIsMobile from 'hooks/useIsMobile'
import { usePersistedToggle, viewportAtLeast } from 'hooks/usePersistedToggle'

interface RightPanelProps {
	/**
	 * Remembers this panel's open/closed choice, per screen. Distinct per screen
	 * on purpose: closing the Tasks panel to get room for the table should not
	 * also close the partner list on Home.
	 */
	storageKey: string
	/** Names the panel for screen readers and the toggle's label. */
	label: string
	/** Extra classes for the scrolling content column, e.g. `overflow-y-auto`. */
	className?: string
	children: React.ReactNode
}

const toggleClass =
	'flex items-center justify-center w-7 h-7 rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm hover:text-[#0080fe] hover:border-[#0080fe]/40 transition-colors'

/**
 * The right-hand panel shared by the desktop screens (MYD-532).
 *
 * Replaces the `hidden lg:flex … w-72` aside that 14 screens each declared,
 * which vanished below 1024px and could never be closed.
 *
 *  - 1024px and up: an inline 288px column that can be closed to a 40px strip
 *    and reopened. Starts closed below 1280px, where the room is worth more to
 *    the page than to the panel. The user's choice is remembered per screen.
 *  - Below 1024px: there is no room for a column at all, so it becomes a drawer
 *    behind a tab on the right edge, and starts closed every visit.
 *
 * The toggle sits ON the panel's left border, outside the content, so it never
 * collides with the content's own header row (counts, links, buttons).
 */
const RightPanel = ({ storageKey, label, className, children }: RightPanelProps) => {
	const isNarrow = useIsMobile()
	const [open, setOpen] = usePersistedToggle(`mdt.panel.${storageKey}`, () => viewportAtLeast(1280))
	const [drawerOpen, setDrawerOpen] = useState(false)

	if (isNarrow) {
		return (
			<>
				<button
					type="button"
					onClick={() => setDrawerOpen(true)}
					aria-label={`Show ${label}`}
					title={`Show ${label}`}
					className="fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center justify-center w-7 h-14 rounded-l-xl border border-r-0 border-gray-200 bg-white text-gray-500 shadow-md hover:text-[#0080fe]"
				>
					<PanelRightOpen className="w-4 h-4" />
				</button>
				<Sheet open={drawerOpen} onClose={() => setDrawerOpen(false)} side="right" className="w-72">
					<div aria-label={label} className={cn('flex-1 min-h-0 flex flex-col', className)}>
						{children}
					</div>
				</Sheet>
			</>
		)
	}

	if (!open) {
		return (
			<aside
				aria-label={label}
				className="flex flex-col items-center w-10 pt-3 border-l border-gray-200 bg-white flex-shrink-0"
			>
				<button
					type="button"
					onClick={() => setOpen(true)}
					aria-label={`Show ${label}`}
					title={`Show ${label}`}
					className={toggleClass}
				>
					<PanelRightOpen className="w-4 h-4" />
				</button>
			</aside>
		)
	}

	return (
		<aside aria-label={label} className="relative flex flex-col w-72 border-l border-gray-200 bg-white flex-shrink-0">
			{/* Straddles the border. Outside the scrolling column, which would
			    otherwise clip it. */}
			<button
				type="button"
				onClick={() => setOpen(false)}
				aria-label={`Hide ${label}`}
				title={`Hide ${label}`}
				className={cn(toggleClass, 'absolute -left-3.5 top-3 z-10')}
			>
				<PanelRightClose className="w-4 h-4" />
			</button>
			<div className={cn('flex-1 min-h-0 flex flex-col', className)}>{children}</div>
		</aside>
	)
}

export default RightPanel
